import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useNotification from 'antd/es/notification/useNotification';
import SkeletonMessages from 'components/skeleton/SkeletonMessages';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import { useConversationMetadata } from 'hooks/useConversations';
import { historicConversationMessagesQueryOptions, patientHistoryQueryOptions } from 'hooks/useLoadPatientHistory';
import { selectCurrentUser } from 'store/users/selectors';
import ConversationTopBar from '../components/chats/ConversationTopBar';
import MessagesList from '../components/chats/MessagesList';
import { colors } from 'design';

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
    backgroundColor: colors.surface.page,
    width: '100%',
  },
  messagesScrollArea: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    maxWidth: 600,
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
});

export default function PatientHistoricConversation() {
  const styles = useStyles();
  const [api, context] = useNotification();
  const { t } = useTranslation();
  const urlParams = useParams();
  const navigate = useNavigate();

  const historicConversationId = urlParams.historicConversation ?? '';

  const currentUser = useSelector(selectCurrentUser);
  const conversationMetadata = useConversationMetadata(urlParams.conversationId ?? '');
  const patientId = conversationMetadata?.conversation.patient.id;

  const queryClient = useQueryClient();

  // Fetch the messages for this historic (read-only) conversation.
  // Using useQuery instead of ahooks useRequest ensures the data lives in the
  // TanStack cache and is not tied to the component's mount/unmount lifecycle.
  const { data, isPending, isError } = useQuery({
    ...historicConversationMessagesQueryOptions(historicConversationId),
    enabled: !!historicConversationId && !!currentUser,
  });

  useEffect(() => {
    if (isError) {
      api.error({ message: t('chats.list.errors.fetch') });
    }
    // api changes identity each render; only react to error state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, t]);

  const messages = data?.messages ?? [];

  // Mark the conversation as read on the server. useMutation (unlike ahooks useRequest)
  // does not cancel its callbacks when the component unmounts — the onSuccess handler
  // that zeros the unread badge will always run, even if the user navigates away while
  // the PATCH is still in flight.
  const markAsReadMutation = useMutation({
    mutationFn: ({ conversationId }: { conversationId: string; patientId: string }) =>
      ApiDoctorConversationsManager.markConversationAsRead(conversationId),
    onSuccess: (_, { conversationId, patientId: pid }) => {
      queryClient.setQueryData(patientHistoryQueryOptions(pid).queryKey, old =>
        old?.map(conv => (conv.id === conversationId ? { ...conv, number_of_unread_messages: 0 } : conv)),
      );
    },
  });

  // Fire mark-as-read once per historicConversationId, as soon as messages are loaded.
  const markedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (data && historicConversationId && patientId && markedRef.current !== historicConversationId) {
      markedRef.current = historicConversationId;
      markAsReadMutation.mutate({ conversationId: historicConversationId, patientId });
    }
    // markAsReadMutation is intentionally omitted: the object identity changes every
    // render but the mutation behaviour is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, historicConversationId, patientId]);

  return (
    <div className={styles.pageContainer}>
      {context}
      <ConversationTopBar
        showBackButton
        showSummaryButton
        avatar={null}
        name={t('chats.subtitle.history')}
        caregiverId={undefined}
        onBackButtonClick={() => {
          navigate(-1);
        }}
      />
      <div className={styles.messagesScrollArea} data-testid="historic-conversation-messages-scroll">
        {isPending ? (
          <SkeletonMessages numberOfTupleMessages={3} />
        ) : (
          <MessagesList key={historicConversationId} messages={messages} swipeToReplyEnabled={false} />
        )}
      </div>
    </div>
  );
}
